import React from 'react';
import { Calendar, Package, MapPin, DollarSign, FileText, CheckCircle, Users, Trash2, X } from 'lucide-react';
import { InternalNotesButton } from './InternalNotesButton';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  internalNotes?: string;
  isGen2Mode?: boolean;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
interface RentalsManagerProps {
  rentals: Rental[];
  filter: string;
  setFilter: (filter: string) => void;
  onRentalClick: (rental: Rental) => void;
  onCreateInvoice: (rental: Rental) => void;
  onAssignTechnician: (rental: Rental) => void;
  creatingInvoice: boolean;
  hasInvoice: (rentalId: string) => boolean;
  getInvoiceAmount: (rentalId: string) => number | null;
  getStatusColor: (status: string) => string;
  getDistributorColor: (distributor: string) => string;
  onNotesUpdated?: () => void;
}
export function RentalsManager({
  rentals,
  filter,
  setFilter,
  onRentalClick,
  onCreateInvoice,
  onAssignTechnician,
  creatingInvoice,
  hasInvoice,
  getInvoiceAmount,
  getStatusColor,
  getDistributorColor,
  onNotesUpdated
}: RentalsManagerProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const filteredRentals = rentals.filter(rental => {
    // Apply status filter
    if (filter !== 'all' && filter !== 'future' && filter !== 'past') {
      if (rental.status !== filter) return false;
    }
    // Apply time-based filter
    if (filter === 'future' || filter === 'past') {
      if (!rental.proposedEventDate) return filter === 'past'; // No date = past
      const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
      if (filter === 'future') {
        return eventDate >= today;
      } else {
        return eventDate < today;
      }
    }
    return true;
  });
  const groupedByMonthYear = filteredRentals.reduce((acc, rental) => {
    if (!rental.proposedEventDate) {
      if (!acc['No Date']) acc['No Date'] = [];
      acc['No Date'].push(rental);
      return acc;
    }
    const date = new Date(rental.proposedEventDate);
    const monthYear = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(rental);
    return acc;
  }, {} as Record<string, Rental[]>);
  const sortedMonthYears = Object.keys(groupedByMonthYear).sort((a, b) => {
    if (a === 'No Date') return 1;
    if (b === 'No Date') return -1;
    const dateA = new Date(groupedByMonthYear[a][0].proposedEventDate);
    const dateB = new Date(groupedByMonthYear[b][0].proposedEventDate);
    return dateA.getTime() - dateB.getTime();
  });
  const formatDateCompact = (dateString: string, days: number) => {
    const startDate = new Date(dateString + 'T00:00:00');
    const month = startDate.toLocaleString('en-US', {
      month: 'short'
    });
    const day = startDate.getDate();
    if (days > 1) {
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + days - 1);
      if (endDate.getMonth() === startDate.getMonth()) {
        return `${month} ${day}-${endDate.getDate()}`;
      } else {
        return `${month} ${day} - ${endDate.toLocaleString('en-US', {
          month: 'short'
        })} ${endDate.getDate()}`;
      }
    }
    return `${month} ${day}`;
  };
  return <>
      {/* Filters */}
      <div className="flex gap-1.5 px-3 py-2 border-b border-slate-200 overflow-x-auto" data-id="element-1675">
        <button onClick={() => setFilter('future')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'future' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1676">
          Future (
          {rentals.filter(r => {
          if (!r.proposedEventDate) return false;
          const d = new Date(r.proposedEventDate + 'T00:00:00');
          return d >= today;
        }).length}
          )
        </button>
        <button onClick={() => setFilter('past')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'past' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1677">
          Past (
          {rentals.filter(r => {
          if (!r.proposedEventDate) return true;
          const d = new Date(r.proposedEventDate + 'T00:00:00');
          return d < today;
        }).length}
          )
        </button>
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1678">
          All ({rentals.length})
        </button>
        <button onClick={() => setFilter('Pending')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'Pending' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1679">
          Pend ({rentals.filter(r => r.status === 'Pending').length})
        </button>
        <button onClick={() => setFilter('Confirmed')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'Confirmed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1680">
          Conf ({rentals.filter(r => r.status === 'Confirmed').length})
        </button>
        <button onClick={() => setFilter('Deposit')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'Deposit' ? 'bg-yellow-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1681">
          Dep ({rentals.filter(r => r.status === 'Deposit').length})
        </button>
        <button onClick={() => setFilter('Paid in Full')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === 'Paid in Full' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1682">
          Paid ({rentals.filter(r => r.status === 'Paid in Full').length})
        </button>
      </div>

      {/* Rentals Content */}
      <div className="px-3 py-2" data-id="element-1683">
        {filteredRentals.length === 0 ? <div className="flex items-center justify-center h-48" data-id="element-1684">
            <div className="text-center" data-id="element-1685">
              <Calendar size={36} className="text-slate-300 mx-auto mb-2" data-id="element-1686" />
              <p className="text-slate-600 text-sm font-medium" data-id="element-1687">
                No rentals found
              </p>
              <p className="text-slate-500 text-xs mt-1" data-id="element-1688">
                {filter !== 'all' ? 'Try changing the filter' : 'Rentals will appear here'}
              </p>
            </div>
          </div> : <div className="space-y-4" data-id="element-1689">
            {sortedMonthYears.map(monthYear => <div key={monthYear} data-id="element-1690">
                <div className="flex items-center gap-2 mb-1.5 px-1" data-id="element-1691">
                  <Calendar size={14} className="text-blue-600" data-id="element-1692" />
                  <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider" data-id="element-1693">
                    {monthYear}
                  </h3>
                  <span className="text-[10px] text-slate-400" data-id="element-1694">
                    ({groupedByMonthYear[monthYear].length})
                  </span>
                </div>
                <div className="divide-y divide-slate-100" data-id="element-1695">
                  {groupedByMonthYear[monthYear].map(rental => {
              const statusBorderColor = rental.status === 'Paid in Full' ? 'border-l-green-500' : rental.status === 'Deposit' ? 'border-l-yellow-500' : rental.status === 'Confirmed' ? 'border-l-blue-500' : 'border-l-slate-300';
              const location = rental.customerInfo?.eventVenueCityState || [rental.customerInfo?.city, rental.customerInfo?.state].filter(Boolean).join(', ');
              return <div key={rental.id} onClick={() => onRentalClick(rental)} className={`py-2 px-2 cursor-pointer hover:bg-blue-50 transition-colors border-l-[3px] ${statusBorderColor}`} data-id="element-1696">
                        {/* Line 1: Date • Event Name • Rings */}
                        <div className="flex items-center gap-1.5 min-w-0" data-id="element-1697">
                          <span className="text-[11px] font-medium text-slate-500 flex-shrink-0 w-14" data-id="element-1698">
                            {rental.proposedEventDate ? formatDateCompact(rental.proposedEventDate, rental.selections.numberOfDays) : '—'}
                          </span>
                          <span className="text-[12px] font-semibold text-slate-800 truncate flex-1" data-id="element-1699">
                            {rental.eventName}
                          </span>
                          {rental.selections.fullGen3Ring > 0 && <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-1 rounded flex-shrink-0" data-id="element-1700">
                              {rental.selections.fullGen3Ring}R
                            </span>}
                          {rental.selections.numberOfDays > 1 && <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-1 rounded flex-shrink-0" data-id="element-1701">
                              {rental.selections.numberOfDays}D
                            </span>}
                          {rental.selections.requireTech && <span className="text-[10px] font-bold text-red-600 flex-shrink-0" data-id="element-1702">
                              T
                            </span>}
                          {hasInvoice(rental.id) && <CheckCircle size={12} className="text-green-600 flex-shrink-0" data-id="element-1703" />}
                        </div>
                        {/* Line 2: Customer • Location • Tech • Actions */}
                        <div className="flex items-center gap-1.5 mt-0.5 min-w-0" data-id="element-1704">
                          <span className="text-[10px] text-slate-500 truncate max-w-[100px]" data-id="element-1705">
                            {rental.name}
                          </span>
                          {location && <>
                              <span className="text-[10px] text-slate-300" data-id="element-1706">
                                •
                              </span>
                              <span className="text-[10px] text-slate-400 truncate max-w-[80px]" data-id="element-1707">
                                {location}
                              </span>
                            </>}
                          {rental.assignedTechnician && rental.assignedTechnician !== 'Choose For Me' && <>
                                <span className="text-[10px] text-slate-300" data-id="element-1708">
                                  •
                                </span>
                                <span className="text-[10px] font-medium text-purple-600 truncate max-w-[70px]" data-id="element-1709">
                                  {rental.assignedTechnician}
                                </span>
                              </>}
                          {/* Spacer to push actions right */}
                          <span className="flex-1" data-id="element-1710" />
                          {/* Inline actions */}
                          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()} data-id="element-1711">
                            <InternalNotesButton rentalId={rental.id} initialNotes={rental.internalNotes} onNotesSaved={onNotesUpdated} data-id="element-1712" />
                            {(rental.selections.requireTech || rental.selections.fullGen3Ring > 0 || rental.selections.gen3Body > 0 || rental.selections.gen3Head > 0 || rental.selections.gen3JudgesOnly > 0) && !hasInvoice(rental.id) && <button onClick={() => onAssignTechnician(rental)} disabled={hasInvoice(rental.id)} title={rental.assignedTechnician ? 'Change Tech' : 'Assign Tech'} className={`p-1 rounded transition-colors ${rental.assignedTechnician ? 'text-purple-500 hover:bg-purple-50' : 'text-orange-500 hover:bg-orange-50'}`} data-id="element-1713">
                                  <Users size={13} data-id="element-1714" />
                                </button>}
                            {hasInvoice(rental.id) ? <span className="text-[10px] font-bold text-green-700" data-id="element-1715">
                                {getInvoiceAmount(rental.id) !== null ? `$${getInvoiceAmount(rental.id)!.toFixed(0)}` : ''}
                              </span> : <button onClick={() => onCreateInvoice(rental)} disabled={creatingInvoice || !rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me'} title="Create Invoice" className={`p-1 rounded transition-colors ${creatingInvoice || !rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me' ? 'text-slate-300 cursor-not-allowed' : 'text-blue-500 hover:bg-blue-50'}`} data-id="element-1716">
                                <FileText size={13} data-id="element-1717" />
                              </button>}
                          </div>
                        </div>
                      </div>;
            })}
                </div>
              </div>)}
          </div>}
      </div>
    </>;
}